/**
 * `/agents/workflows/compiled/$slug` — RO-3-02c / #44399.
 *
 * Port of `excellent/app/agents/workflows/compiled/[slug]/page.tsx` (42 LOC) +
 * `_client.tsx` (1,806 LOC). No new endpoints: every contract this surface
 * drives (`compilations.list/get/adjudicate/setStatus/source/epoch/promote/
 * demote/getAutorun/setAutorun`) was already mounted on the Hono host by
 * RO-1-10 (`excellent/server/routes/models.ts`). The only structural change a
 * Vite client forces is that the server-rendered `getCompilation(...)` initial
 * becomes a `useApi(compilations.get, …)` load — SWR stays the data layer
 * (PRD D3); only the transport under it changed.
 *
 * A compiled workflow is a DIFFERENT primitive from a loop (an agentic decision
 * mined into rule-data, not an optimizable prompt pipeline). It is its own
 * page, not a tab of the Studio — that distinction is preserved.
 *
 * RO-3-02b deferred this page once (pending placeholder in dynamic-segments);
 * this file is the real route. Literal `compiled` outranks the `$slug` sibling
 * registered by `agent-workflow-detail.tsx`.
 */

import * as React from "react";
import { createRoute } from "@tanstack/react-router";
import {
  Alert,
  Button,
  Card,
  CardList,
  DataTable,
  DropdownMenu,
  HealthBar,
  IntentBadge,
  PageHeader,
  RowShell,
  Sparkline,
  Switch,
  Tabs,
  TabsContent,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  cn,
  formatRelativeTime,
  formatDateTime,
  type DataTableColumn,
} from "@excellent/ui";
import { apiCall, apiKey, useApi } from "@excellent/ui/api";
import { useSWRConfig } from "swr";
import {
  compilations,
  type AdjudicateBody,
  type CompilationDetail,
  type CorpusSampleRow,
  type DisputeView,
  type EpochView,
  type RuleView,
} from "@excellent/ui/api/contracts/compilations";
import {
  ArrowLeft,
  Check,
  ChevronLeft,
  ChevronRight,
  Ellipsis,
  Keyboard,
  Maximize2,
  Minimize2,
  RotateCcw,
  SkipForward,
} from "@excellent/ui/icons";
import { NavTabs, type NavTab } from "@excellent/ui";
import {
  buildDisputeClusters,
  type DisputeCluster,
} from "@/compile/clusters";
import { resumeStatusAfterPause } from "@/compile/status";
import { rootRoute } from "./__root";

// ── formatting ────────────────────────────────────────────────────────────────

const NONE = "∅";
function pct0(x: number): string {
  return `${Math.round(x * 100)}%`;
}
function pct1(x: number): string {
  return `${(x * 100).toFixed(1)}%`;
}
function toValue(label: string): string | null {
  return label === NONE ? null : label;
}
function cap(v: string): string {
  return v
    .split("-")
    .map((w) => (w ? w.charAt(0).toUpperCase() + w.slice(1) : w))
    .join("-");
}

type BadgeIntent = "info" | "success" | "warning" | "danger" | "neutral";
const STATUS_INTENT: Record<CompilationDetail["status"], BadgeIntent> = {
  compiled: "success",
  compiling: "info",
  sourcing: "info",
  defined: "neutral",
  paused: "warning",
};
const RULE_INTENT: Record<RuleView["status"], BadgeIntent> = {
  accepted: "success",
  candidate: "info",
  rejected: "danger",
  retired: "neutral",
};
const SPLIT_INTENT: Record<"train" | "holdout", BadgeIntent> = {
  train: "neutral",
  holdout: "info",
};

// ── axis display copy (fallback for unknown axes) ─────────────────────────────

interface AxisCopy {
  question: string;
  noun: string;
  why: string;
}
const AXIS_COPY: Record<string, AxisCopy> = {
  kind: {
    question: "What kind of work is this?",
    noun: "Kind",
    why: "the two labelers disagreed on the type of work",
  },
  section: {
    question: "Which team owns this?",
    noun: "Team",
    why: "few rules cover which team owns a task, so most come to you",
  },
  area: {
    question: "Which part of the product is it?",
    noun: "Area",
    why: "product-area rules barely fire — nearly every one lands here",
  },
  goalId: {
    question: "Which goal does this belong to?",
    noun: "Goal",
    why: "linking a task to a goal is high-stakes, so it always asks",
  },
};
function axisNoun(axis: string): string {
  return AXIS_COPY[axis]?.noun ?? cap(axis);
}
function axisQuestion(axis: string): string {
  return AXIS_COPY[axis]?.question ?? `Which ${axisNoun(axis).toLowerCase()}?`;
}
function axisWhy(axis: string): string {
  return AXIS_COPY[axis]?.why ?? "the labelers disagreed, so it comes to you";
}

const PILL_TABS: readonly NavTab[] = [
  { href: "adjudicate", label: "Label" },
  { href: "health", label: "Health" },
  { href: "rules", label: "Rules" },
  { href: "epochs", label: "Runs" },
  { href: "corpus", label: "Data" },
];

const LADDER = [
  { key: "auto", label: "Rules decide", fill: "bg-success", dot: "bg-success" },
  { key: "tier2", label: "Cheap model decides", fill: "bg-info", dot: "bg-info" },
  { key: "human", label: "Lands on you", fill: "bg-warning", dot: "bg-warning" },
] as const;

// ── component ─────────────────────────────────────────────────────────────────

export function CompiledDetail({
  slug,
  initial,
}: {
  slug: string;
  initial: CompilationDetail;
}) {
  const { mutate } = useSWRConfig();
  const { data } = useApi(
    compilations.get,
    { params: { slug } },
    { fallbackData: initial },
  );
  const d = data ?? initial;

  const [tab, setTab] = React.useState(
    d.disputes.length > 0 ? "adjudicate" : "health",
  );
  const [busy, setBusy] = React.useState<string | null>(null);
  const [actionMsg, setActionMsg] = React.useState<string | null>(null);
  const [promoteOpen, setPromoteOpen] = React.useState(false);
  const [autorunOpen, setAutorunOpen] = React.useState(false);
  // Pre-pause status for honest resume (PRD A8): never derive resume target from
  // lastEpoch — a compiling workflow with epochs must resume as compiling.
  const [statusBeforePause, setStatusBeforePause] = React.useState<
    CompilationDetail["status"] | null
  >(null);
  // Optimistic settle map — cleared when revalidate drops the disputes. Never
  // the sole source of truth after reload (PRD §7).
  const [optimistic, setOptimistic] = React.useState<Record<string, string | null>>({});

  const revalidate = React.useCallback(() => {
    void mutate(apiKey(compilations.get, { params: { slug } }));
  }, [mutate, slug]);

  React.useEffect(() => {
    // Drop optimistic entries whose disputes are gone after revalidate.
    const openKeys = new Set(
      buildDisputeClusters(d.disputes).map((c) => c.key),
    );
    setOptimistic((prev) => {
      let changed = false;
      const next = { ...prev };
      for (const k of Object.keys(next)) {
        if (!openKeys.has(k)) {
          delete next[k];
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [d.disputes]);

  async function adjudicate(
    exampleKeys: string[],
    axis: string,
    value: string | null,
    clusterKey: string,
  ) {
    const exampleKey = exampleKeys[0];
    if (exampleKey === undefined) return;
    setBusy(clusterKey);
    setOptimistic((s) => ({ ...s, [clusterKey]: value }));
    try {
      const body: AdjudicateBody =
        exampleKeys.length === 1
          ? { exampleKey, axis, value }
          : { exampleKey, axis, value, exampleKeys };
      await apiCall(compilations.adjudicate, { params: { slug }, body });
      revalidate();
    } catch (err) {
      setOptimistic((s) => {
        const next = { ...s };
        delete next[clusterKey];
        return next;
      });
      setActionMsg(err instanceof Error ? err.message : "Adjudicate failed");
    } finally {
      setBusy(null);
    }
  }

  async function onLiveToggle(wantLive: boolean) {
    if (wantLive) {
      setPromoteOpen(true);
      return;
    }
    setBusy("demote");
    try {
      await apiCall(compilations.demote, { params: { slug } });
      revalidate();
    } catch (err) {
      setActionMsg(err instanceof Error ? err.message : "Demote failed");
    } finally {
      setBusy(null);
    }
  }

  async function onPauseWorkflow() {
    setBusy("status");
    try {
      if (d.status === "paused") {
        // Resume the status captured at pause time (PRD A8) — never lastEpoch.
        const target = resumeStatusAfterPause(statusBeforePause);
        await apiCall(compilations.setStatus, {
          params: { slug },
          body: { status: target },
        });
        setStatusBeforePause(null);
        setActionMsg(`Resumed (${target})`);
        revalidate();
      } else {
        // Capture the live status BEFORE pausing so resume is honest.
        setStatusBeforePause(d.status);
        await apiCall(compilations.setStatus, {
          params: { slug },
          body: { status: "paused" },
        });
        setActionMsg("Paused — autorun will skip this workflow");
        revalidate();
      }
    } catch (err) {
      setActionMsg(err instanceof Error ? err.message : "Status change failed");
    } finally {
      setBusy(null);
    }
  }

  async function onRunPass() {
    setBusy("epoch");
    try {
      await apiCall(compilations.epoch, { params: { slug } });
      setActionMsg("Pass dispatched — results land when the epoch finishes");
      revalidate();
    } catch (err) {
      setActionMsg(err instanceof Error ? err.message : "Epoch dispatch failed");
    } finally {
      setBusy(null);
    }
  }

  async function onAddExamples() {
    setBusy("source");
    try {
      await apiCall(compilations.source, { params: { slug }, body: {} });
      setActionMsg("Sourcing dispatched — corpus grows when the run finishes");
      revalidate();
    } catch (err) {
      setActionMsg(err instanceof Error ? err.message : "Source dispatch failed");
    } finally {
      setBusy(null);
    }
  }

  async function copyCli() {
    const text = d.sourceCommand;
    try {
      await navigator.clipboard.writeText(text);
      setActionMsg(`Copied: ${text}`);
    } catch {
      setActionMsg(text);
    }
  }

  const isLive = !!d.promoted.at;
  const clusters = React.useMemo(
    () => buildDisputeClusters(d.disputes),
    [d.disputes],
  );
  const openClusterCount = clusters.filter((c) => !(c.key in optimistic)).length;

  const shortDesc =
    d.charter.length > 160 ? d.charter.slice(0, 157).trimEnd() + "…" : d.charter;

  return (
    <div className="@container/page mx-auto flex max-w-[1180px] flex-col gap-5">
      <div className="flex flex-col gap-2">
        <a
          href="/agents/workflows"
          className="group inline-flex w-fit items-center gap-1 type-body text-muted-foreground transition-colors hover:text-foreground"
        >
          <ArrowLeft className="size-4" />
          Workflows
        </a>
        <div className="flex items-start justify-between gap-6">
          <div className="flex min-w-0 flex-col gap-2">
            <PageHeader
              title={d.title}
              description={shortDesc}
              toolbar={
                <div className="flex items-center gap-2">
                  <IntentBadge intent={STATUS_INTENT[d.status]}>{d.status}</IntentBadge>
                  <LiveSwitch
                    on={isLive}
                    disabled={busy === "demote"}
                    onChange={onLiveToggle}
                  />
                  <DropdownMenu
                    items={[
                      {
                        label: "Run a Pass",
                        onSelect: () => void onRunPass(),
                      },
                      {
                        label: "Add examples",
                        onSelect: () => void onAddExamples(),
                      },
                      {
                        label: isLive ? "Take offline…" : "Take it live…",
                        onSelect: () => {
                          if (isLive) void onLiveToggle(false);
                          else setPromoteOpen(true);
                        },
                      },
                      {
                        label: "Autorun settings…",
                        onSelect: () => setAutorunOpen(true),
                      },
                      { type: "separator" },
                      {
                        label: "Copy CLI command",
                        onSelect: () => void copyCli(),
                      },
                      { type: "separator" },
                      {
                        label:
                          d.status === "paused"
                            ? "Resume this workflow"
                            : "Pause this workflow",
                        onSelect: () => void onPauseWorkflow(),
                        destructive: d.status !== "paused",
                      },
                    ]}
                  >
                    <Button
                      variant="outline"
                      size="icon-tb"
                      aria-label="More actions"
                      disabled={busy != null}
                    >
                      <Ellipsis />
                    </Button>
                  </DropdownMenu>
                </div>
              }
            />
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 type-subtext text-muted-foreground">
              <span>{d.axes.length} axes</span>
              <span aria-hidden>·</span>
              <span>auto ≥ {pct0(d.autoThreshold)} confidence</span>
              {!d.inStore && (
                <>
                  <span aria-hidden>·</span>
                  <span>
                    Not compiled yet — run{" "}
                    <code className="rounded-xs bg-muted px-1 py-0.5 font-mono text-foreground/80">
                      {d.sourceCommand}
                    </code>
                  </span>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {actionMsg && (
        <Alert className="flex items-center gap-2 px-3 py-2 type-subtext text-foreground">
          <span className="flex-1">{actionMsg}</span>
          <Button variant="ghost" size="sm" onClick={() => setActionMsg(null)}>
            Dismiss
          </Button>
        </Alert>
      )}

      <NavTabs
        tabs={PILL_TABS.map((t): NavTab =>
          t.href === "adjudicate" && openClusterCount > 0
            ? { ...t, count: openClusterCount }
            : t,
        )}
        value={tab}
        onSelect={setTab}
        className="flex-wrap"
      />

      <Tabs value={tab} onValueChange={setTab} className="gap-4">
        <TabsContent value="adjudicate">
          <LabelingStudio
            detail={d}
            clusters={clusters}
            optimistic={optimistic}
            busy={busy}
            onSettle={(cluster, value) => {
              const keys = cluster.members.map((m) => m.exampleKey);
              void adjudicate(keys, cluster.axis, value, cluster.key);
            }}
            onUndoOptimistic={(key) =>
              setOptimistic((s) => {
                const next = { ...s };
                delete next[key];
                return next;
              })
            }
            realDisputes={d.disputes.filter((x) => x.origin === "real")}
            onAdjudicateReal={(disp, value) =>
              void adjudicate(
                [disp.exampleKey],
                disp.axis,
                value,
                `${disp.exampleKey}:${disp.axis}`,
              )
            }
          />
        </TabsContent>
        <TabsContent value="health" className="flex flex-col gap-4">
          <HealthTab detail={d} />
        </TabsContent>
        <TabsContent value="rules" className="flex flex-col gap-4">
          <RulesTab detail={d} onGoLabel={() => setTab("adjudicate")} />
        </TabsContent>
        <TabsContent value="epochs" className="flex flex-col gap-4">
          <EpochsTab epochs={d.epochs} />
        </TabsContent>
        <TabsContent value="corpus" className="flex flex-col gap-4">
          <CorpusTab detail={d} />
        </TabsContent>
      </Tabs>

      <PromoteDialog
        open={promoteOpen}
        onClose={() => setPromoteOpen(false)}
        slug={slug}
        onDone={() => {
          setPromoteOpen(false);
          setActionMsg("Live — rules now apply to new work");
          revalidate();
        }}
        onError={(m) => setActionMsg(m)}
      />
      <AutorunDialog
        open={autorunOpen}
        initial={d.autorun}
        onClose={() => setAutorunOpen(false)}
        onDone={() => {
          setAutorunOpen(false);
          setActionMsg("Autorun settings saved");
          revalidate();
        }}
      />
    </div>
  );
}

// ── Live switch ───────────────────────────────────────────────────────────────

function LiveSwitch({
  on,
  disabled,
  onChange,
}: {
  on: boolean;
  disabled?: boolean;
  onChange: (on: boolean) => void;
}) {
  return (
    <label
      className={cn(
        "flex h-7 cursor-pointer items-center gap-2 rounded-md border px-2.5 transition-colors",
        on ? "border-success/50 bg-success/5" : "border-border",
        disabled && "pointer-events-none opacity-50",
      )}
    >
      <Switch
        checked={on}
        onCheckedChange={onChange}
        size="sm"
        className="data-[state=checked]:bg-success"
        disabled={disabled}
      />
      <span className={cn("type-body font-medium", on ? "text-success" : "text-muted-foreground")}>
        {on ? "Live" : "Paused"}
      </span>
    </label>
  );
}

// ── Promote / Autorun dialogs ─────────────────────────────────────────────────

function PromoteDialog({
  open,
  onClose,
  slug,
  onDone,
  onError,
}: {
  open: boolean;
  onClose: () => void;
  slug: string;
  onDone: () => void;
  onError: (m: string) => void;
}) {
  const [taskId, setTaskId] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  async function submit() {
    const n = Number(taskId);
    if (!Number.isInteger(n) || n <= 0) {
      onError("Enter a verified task id (status=done) that names this workflow");
      return;
    }
    setBusy(true);
    try {
      await apiCall(compilations.promote, {
        params: { slug },
        body: { taskId: n },
      });
      onDone();
    } catch (err) {
      onError(err instanceof Error ? err.message : "Promotion refused");
    } finally {
      setBusy(false);
    }
  }
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Take it live</DialogTitle>
        </DialogHeader>
        <p className="type-body text-muted-foreground">
          Promotion requires a ship/verify-<em>done</em> task whose title or
          description names <code className="type-subtext">{slug}</code> (invariant 9 —
          the compiler may not promote its own work).
        </p>
        <div className="flex flex-col gap-1.5">
          <span className="type-subtext font-medium text-foreground">Verified task id</span>
          <Input
            type="number"
            min={1}
            value={taskId}
            onChange={(e) => setTaskId(e.target.value)}
            placeholder="e.g. 43563"
          />
        </div>
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="default" disabled={busy} onClick={() => void submit()}>
            Promote
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function AutorunDialog({
  open,
  initial,
  onClose,
  onDone,
}: {
  open: boolean;
  initial: CompilationDetail["autorun"];
  onClose: () => void;
  onDone: () => void;
}) {
  const [enabled, setEnabled] = React.useState(initial.enabled);
  const [budget, setBudget] = React.useState(String(initial.budgetTokens));
  const [busy, setBusy] = React.useState(false);
  React.useEffect(() => {
    setEnabled(initial.enabled);
    setBudget(String(initial.budgetTokens));
  }, [initial, open]);
  async function save() {
    setBusy(true);
    try {
      const n = Number(budget);
      await apiCall(compilations.setAutorun, {
        body: {
          enabled,
          budgetTokens: Number.isFinite(n) && n > 0 ? Math.floor(n) : undefined,
        },
      });
      onDone();
    } catch {
      /* dialog stays open */
    } finally {
      setBusy(false);
    }
  }
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Autorun settings</DialogTitle>
        </DialogHeader>
        <p className="type-body text-muted-foreground">
          Workspace-wide. When on, autopilot may dispatch at most one compile
          epoch per day under the token ceiling (OFF by default).
        </p>
        <label className="flex items-center gap-2 type-body">
          <Switch checked={enabled} onCheckedChange={setEnabled} size="sm" />
          Enable compile autorun
        </label>
        <div className="flex flex-col gap-1.5">
          <span className="type-subtext font-medium text-foreground">Per-epoch token budget</span>
          <Input
            type="number"
            min={1000}
            value={budget}
            onChange={(e) => setBudget(e.target.value)}
          />
        </div>
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="default" disabled={busy} onClick={() => void save()}>
            Save
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ── Labeling studio ───────────────────────────────────────────────────────────

type Cluster = DisputeCluster<DisputeView>;

function LabelingStudio({
  detail,
  clusters,
  optimistic,
  busy,
  onSettle,
  onUndoOptimistic,
  realDisputes,
  onAdjudicateReal,
}: {
  detail: CompilationDetail;
  clusters: Cluster[];
  optimistic: Record<string, string | null>;
  busy: string | null;
  onSettle: (cluster: Cluster, value: string | null) => void;
  onUndoOptimistic: (key: string) => void;
  realDisputes: DisputeView[];
  onAdjudicateReal: (d: DisputeView, value: string | null) => void;
}) {
  const [mode, setMode] = React.useState<"focus" | "list">("focus");
  const [cursor, setCursor] = React.useState(0);
  const [expanded, setExpanded] = React.useState(false);
  const [last, setLast] = React.useState<{
    key: string;
    value: string | null;
    n: number;
  } | null>(null);

  if (clusters.length === 0 && realDisputes.length === 0) {
    return (
      <Card className="gap-0 py-0">
        <p className="p-6 type-body text-muted-foreground">
          {detail.corpusTotal === 0
            ? `Not compiled yet — run \`${detail.sourceCommand}\` to source a corpus, then run an epoch. Disputes the labelers split on will surface here.`
            : "Nothing to refine. The labelers agree (or you've settled the splits) — every metric on this page reflects the current ground truth."}
        </p>
      </Card>
    );
  }

  const open = clusters.filter((c) => !(c.key in optimistic));
  const current = clusters[cursor] ?? clusters[0];
  const go = (delta: number) =>
    setCursor((c) => Math.max(0, Math.min(clusters.length - 1, c + delta)));

  const advance = React.useCallback(
    (from: number) => {
      const next = clusters.findIndex((c, i) => i > from && !(c.key in optimistic));
      setCursor(
        next === -1 ? clusters.findIndex((c) => !(c.key in optimistic)) : next,
      );
    },
    [clusters, optimistic],
  );

  const settle = React.useCallback(
    (cluster: Cluster, value: string | null) => {
      onSettle(cluster, value);
      setLast({ key: cluster.key, value, n: cluster.members.length });
      advance(clusters.indexOf(cluster));
    },
    [advance, clusters, onSettle],
  );

  React.useEffect(() => {
    if (mode !== "focus") return;
    function onKey(e: KeyboardEvent) {
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const target = e.target as HTMLElement | null;
      if (target && ["INPUT", "TEXTAREA", "SELECT"].includes(target.tagName)) return;
      const c = clusters[cursor];
      if (e.key === "Escape" && expanded) setExpanded(false);
      else if (!c) return;
      else if (e.key === "1") settle(c, c.a === NONE ? null : c.a);
      else if (e.key === "2") settle(c, c.b === NONE ? null : c.b);
      else if (e.key.toLowerCase() === "s") advance(cursor);
      else if (e.key.toLowerCase() === "u" && last) {
        onUndoOptimistic(last.key);
        setCursor(clusters.findIndex((x) => x.key === last.key));
        setLast(null);
      } else return;
      e.preventDefault();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [advance, clusters, cursor, expanded, last, mode, onUndoOptimistic, settle]);

  return (
    <div className="flex flex-col gap-3">
      <LabelStatsBar
        detail={detail}
        clusters={clusters}
        optimistic={optimistic}
        mode={mode}
        onMode={setMode}
      />

      {realDisputes.length > 0 && (
        <Card className="gap-0 py-0">
          <div className="px-5 py-3">
            <IntentBadge intent="warning">from your live data</IntentBadge>
            <p className="mt-2 type-subtext text-muted-foreground">
              Real-origin disputes are never batch-grouped — each is your own past decision.
            </p>
            {realDisputes.map((disp) => (
              <RealDisputeRow
                key={`${disp.exampleKey}:${disp.axis}`}
                dispute={disp}
                detail={detail}
                busy={busy === `${disp.exampleKey}:${disp.axis}`}
                onPick={(v) => onAdjudicateReal(disp, v)}
              />
            ))}
          </div>
        </Card>
      )}

      {last && (
        <Alert className="flex items-center gap-3 px-3 py-2 type-subtext">
          <Check className="size-3.5 text-success" />
          <span className="text-foreground">
            Settled {last.n} example{last.n === 1 ? "" : "s"} as{" "}
            <span className="font-medium">{last.value ? cap(last.value) : "None"}</span>
            {optimistic[last.key] !== undefined
              ? " (writing…)"
              : " — reloading ground truth"}
            .
          </span>
          <Button
            variant="ghost"
            size="sm"
            className="ml-auto"
            onClick={() => {
              onUndoOptimistic(last.key);
              setCursor(clusters.findIndex((x) => x.key === last.key));
              setLast(null);
            }}
          >
            <RotateCcw />
            Undo
          </Button>
        </Alert>
      )}

      {mode === "focus" ? (
        open.length > 0 && current ? (
          <FocusCard
            detail={detail}
            clusters={clusters}
            optimistic={optimistic}
            cursor={Math.min(cursor, Math.max(0, clusters.length - 1))}
            onPick={setCursor}
            cluster={current}
            index={Math.min(cursor, Math.max(0, clusters.length - 1))}
            total={clusters.length}
            expanded={expanded}
            answeredValue={
              current.key in optimistic ? optimistic[current.key] : undefined
            }
            busy={busy === current.key}
            onSettle={(value) => settle(current, value)}
            onSkip={() => advance(cursor)}
            onUndo={() => {
              onUndoOptimistic(current.key);
              if (last?.key === current.key) setLast(null);
            }}
            onPrev={() => go(-1)}
            onNext={() => go(1)}
            onToggleExpand={() => setExpanded((v) => !v)}
          />
        ) : clusters.length === 0 ? null : (
          <Card className="flex min-h-[12rem] items-center justify-center p-10">
            <div className="flex flex-col items-center gap-2 text-center">
              <Check className="size-5 text-success-fg" />
              <p className="type-body text-foreground">Queue clear.</p>
              <p className="max-w-sm type-subtext text-muted-foreground">
                Every split is settled. The next Pass will fold your answers into
                the rules and re-score both splits.
              </p>
            </div>
          </Card>
        )
      ) : (
        <ListMode
          clusters={clusters}
          optimistic={optimistic}
          busy={busy}
          onSettle={settle}
        />
      )}
    </div>
  );
}

function LabelStatsBar({
  detail,
  clusters,
  optimistic,
  mode,
  onMode,
}: {
  detail: CompilationDetail;
  clusters: Cluster[];
  optimistic: Record<string, string | null>;
  mode: "focus" | "list";
  onMode: (m: "focus" | "list") => void;
}) {
  const cover = new Map(detail.axisCoverage.map((c) => [c.axis, c]));
  const perQuestion = detail.axes
    .map((a) => {
      const all = clusters.filter((c) => c.axis === a.key);
      const remaining = all.filter((c) => !(c.key in optimistic)).length;
      return {
        axis: a.key,
        total: all.length,
        remaining,
        coverage: cover.get(a.key)?.coverage ?? 0,
      };
    })
    .filter((q) => q.total > 0)
    .sort((x, y) => y.remaining - x.remaining);

  return (
    <Card className="gap-0 overflow-hidden py-0">
      <div className="flex flex-col divide-y divide-border/60 @2xl/page:flex-row @2xl/page:divide-x @2xl/page:divide-y-0">
        {perQuestion.map((q) => (
          <div
            key={q.axis}
            title={axisWhy(q.axis)}
            className="flex min-w-0 flex-1 flex-col gap-2 px-5 py-3.5"
          >
            <div className="flex items-baseline justify-between gap-2">
              <span className="type-body font-medium text-foreground">{axisNoun(q.axis)}</span>
              <span className="type-subtext tabular-nums text-muted-foreground">
                <span className="text-foreground">{q.remaining}</span> left
              </span>
            </div>
            <HealthBar
              value={q.coverage}
              max={1}
              intent={q.coverage >= 0.6 ? "success" : q.coverage >= 0.2 ? "warning" : "danger"}
            />
            <span className="type-subtext tabular-nums text-muted-foreground">
              rules cover {pct0(q.coverage)}
            </span>
          </div>
        ))}
        <div className="flex shrink-0 items-center justify-center px-4 py-3.5">
          <div className="flex items-center gap-1 rounded-md border border-border p-1">
            <Button
              variant={mode === "focus" ? "secondary" : "ghost"}
              size="sm"
              onClick={() => onMode("focus")}
            >
              One at a time
            </Button>
            <Button
              variant={mode === "list" ? "secondary" : "ghost"}
              size="sm"
              onClick={() => onMode("list")}
            >
              Whole list
            </Button>
          </div>
        </div>
      </div>
    </Card>
  );
}

function RealDisputeRow({
  dispute,
  detail,
  busy,
  onPick,
}: {
  dispute: DisputeView;
  detail: CompilationDetail;
  busy: boolean;
  onPick: (v: string | null) => void;
}) {
  const axis = detail.axes.find((a) => a.key === dispute.axis);
  return (
    <div className="space-y-2 border-b border-border/40 py-4 last:border-b-0">
      <div className="flex flex-wrap items-center gap-2">
        <IntentBadge intent="info">{axisNoun(dispute.axis)}</IntentBadge>
        <span className="type-subtext tabular-nums text-muted-foreground">{dispute.exampleKey}</span>
      </div>
      <p className="type-body font-medium text-foreground">{dispute.title}</p>
      <div className="flex flex-wrap items-center gap-2">
        <Button
          variant="outline"
          size="sm"
          disabled={busy}
          onClick={() => onPick(toValue(dispute.valueGenerated))}
        >
          {cap(dispute.valueGenerated)}
        </Button>
        <span className="type-subtext text-muted-foreground">or</span>
        <Button
          variant="outline"
          size="sm"
          disabled={busy}
          onClick={() => onPick(toValue(dispute.valueLlm))}
        >
          {cap(dispute.valueLlm)}
        </Button>
        {axis?.noneAllowed && (
          <Button variant="ghost" size="sm" disabled={busy} onClick={() => onPick(null)}>
            none
          </Button>
        )}
      </div>
    </div>
  );
}

function FocusCard({
  detail,
  clusters,
  optimistic,
  cursor,
  onPick,
  cluster,
  index,
  total,
  expanded,
  answeredValue,
  busy,
  onSettle,
  onSkip,
  onUndo,
  onPrev,
  onNext,
  onToggleExpand,
}: {
  detail: CompilationDetail;
  clusters: Cluster[];
  optimistic: Record<string, string | null>;
  cursor: number;
  onPick: (i: number) => void;
  cluster: Cluster;
  index: number;
  total: number;
  expanded: boolean;
  answeredValue: string | null | undefined;
  busy: boolean;
  onSettle: (value: string | null) => void;
  onSkip: () => void;
  onUndo: () => void;
  onPrev: () => void;
  onNext: () => void;
  onToggleExpand: () => void;
}) {
  const spec = detail.axes.find((a) => a.key === cluster.axis);
  const others = (spec?.values ?? []).filter((v) => v !== cluster.a && v !== cluster.b);
  const n = cluster.members.length;
  const answered = answeredValue !== undefined;
  const [changing, setChanging] = React.useState(false);
  React.useEffect(() => setChanging(false), [cluster.key]);

  const body = (
    <Card
      className={cn(
        "flex flex-col gap-0 overflow-hidden py-0 ring-1 ring-border",
        expanded ? "h-full" : "min-h-[28rem]",
      )}
    >
      <div className="flex flex-wrap items-center gap-2 border-b border-border bg-muted/40 px-5 py-2.5">
        <span className="type-body font-medium text-foreground">Labeling</span>
        <div className="ml-auto flex items-center gap-1">
          <Button variant="ghost" size="icon-sm" onClick={onPrev} disabled={index <= 0} aria-label="Previous">
            <ChevronLeft />
          </Button>
          <span className="min-w-14 text-center type-subtext tabular-nums text-muted-foreground">
            {index + 1} / {total}
          </span>
          <Button
            variant="ghost"
            size="icon-sm"
            onClick={onNext}
            disabled={index >= total - 1}
            aria-label="Next"
          >
            <ChevronRight />
          </Button>
          <span className="mx-1 h-4 w-px bg-border" />
          <Button
            variant="ghost"
            size="icon-sm"
            title="1 / 2 — answer · S — skip · U — undo (optimistic, before revalidate)"
            aria-label="Keyboard shortcuts"
          >
            <Keyboard />
          </Button>
          <Button
            variant="ghost"
            size="icon-sm"
            onClick={onToggleExpand}
            aria-label={expanded ? "Collapse" : "Expand"}
          >
            {expanded ? <Minimize2 /> : <Maximize2 />}
          </Button>
        </div>
      </div>

      <div className="flex min-h-0 flex-1 flex-col @2xl/page:flex-row">
        <div className="flex max-h-52 shrink-0 flex-col overflow-y-auto border-b border-border @2xl/page:max-h-none @2xl/page:w-64 @2xl/page:border-b-0 @2xl/page:border-r">
          <QueuePanel
            clusters={clusters}
            optimistic={optimistic}
            cursor={cursor}
            onPick={onPick}
          />
        </div>

        <div className={cn("flex min-h-0 min-w-0 flex-1 flex-col", answered && "bg-success/5")}>
          <div
            className={cn(
              "flex flex-wrap items-center gap-2 border-b px-5 py-2.5",
              answered ? "border-success/30 bg-success/10" : "border-border",
            )}
          >
            <IntentBadge intent="info">{axisNoun(cluster.axis)}</IntentBadge>
            {cluster.blast === "high" && (
              <IntentBadge intent="warning">changes a link — you decide</IntentBadge>
            )}
            <span className="type-subtext text-muted-foreground">
              settles {n} example{n === 1 ? "" : "s"}
            </span>
            {answered && (
              <span className="ml-auto flex items-center gap-1.5 type-subtext font-medium text-success-fg">
                <Check className="size-3.5" />
                Labeled {answeredValue ? cap(answeredValue) : "None"}
              </span>
            )}
          </div>

          <div className="flex min-h-0 flex-1 flex-col gap-4 p-5">
            <h3 className={cn("font-medium text-foreground", expanded ? "type-heading" : "type-title")}>
              {axisQuestion(cluster.axis)}
            </h3>
            <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
              {cluster.members.map((m) => (
                <RowShell key={m.exampleKey} className="flex-col items-start gap-0.5">
                  <p className="type-body text-foreground">{m.title}</p>
                  {m.description && (
                    <p className="line-clamp-2 type-subtext text-muted-foreground">{m.description}</p>
                  )}
                </RowShell>
              ))}
            </div>

            {answered && !changing ? (
              <div className="flex flex-wrap items-center justify-between gap-2">
                <Button variant="ghost" onClick={onUndo} disabled={busy}>
                  <RotateCcw />
                  Undo
                </Button>
                <Button variant="outline" size="sm" onClick={() => setChanging(true)}>
                  Change
                </Button>
              </div>
            ) : (
              <div className="flex flex-wrap items-center gap-2">
                <Button variant="ghost" onClick={onSkip} disabled={busy}>
                  <SkipForward />
                  Skip
                </Button>
                <Button
                  variant="outline"
                  disabled={busy}
                  onClick={() => onSettle(cluster.a === NONE ? null : cluster.a)}
                >
                  {cap(cluster.a)}
                  <Kbd>1</Kbd>
                </Button>
                <Button
                  variant="outline"
                  disabled={busy}
                  onClick={() => onSettle(cluster.b === NONE ? null : cluster.b)}
                >
                  {cap(cluster.b)}
                  <Kbd>2</Kbd>
                </Button>
                {(others.length > 0 || spec?.noneAllowed) && (
                  // The empty `value` is never a SelectItem — it holds the
                  // trigger on its "other…" placeholder after every pick, which
                  // is what the raw <select> did by re-rendering value="".
                  <Select
                    value=""
                    disabled={busy}
                    onValueChange={(v) => {
                      if (!v) return;
                      onSettle(toValue(v));
                    }}
                  >
                    <SelectTrigger
                      className="w-auto"
                      aria-label={`Other value for ${cluster.axis}`}
                    >
                      <SelectValue placeholder="other…" />
                    </SelectTrigger>
                    <SelectContent>
                      {spec?.noneAllowed && <SelectItem value={NONE}>none / ∅</SelectItem>}
                      {others.map((v) => (
                        <SelectItem key={v} value={v}>
                          {v}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </Card>
  );

  if (!expanded) return body;
  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-background p-4">
      {body}
    </div>
  );
}

function QueuePanel({
  clusters,
  optimistic,
  cursor,
  onPick,
}: {
  clusters: Cluster[];
  optimistic: Record<string, string | null>;
  cursor: number;
  onPick: (i: number) => void;
}) {
  const byAxis = new Map<string, Cluster[]>();
  for (const c of clusters) {
    const list = byAxis.get(c.axis) ?? [];
    list.push(c);
    byAxis.set(c.axis, list);
  }
  const activeAxis = clusters[cursor]?.axis;
  const [openAxes, setOpenAxes] = React.useState<Record<string, boolean>>({});
  const isOpen = (axis: string) => openAxes[axis] ?? axis === activeAxis;

  return (
    <div className="flex flex-col">
      {[...byAxis.entries()].map(([axis, list]) => {
        const left = list.filter((c) => !(c.key in optimistic)).length;
        const groupOpen = isOpen(axis);
        return (
          <div key={axis} className="border-b border-border/60 last:border-b-0">
            <button
              type="button"
              onClick={() => setOpenAxes((o) => ({ ...o, [axis]: !groupOpen }))}
              className={cn(
                "flex w-full items-center gap-2 px-4 py-2.5 text-left transition-colors hover:bg-muted/60",
                axis === activeAxis && "bg-muted/40",
              )}
            >
              <ChevronRight
                className={cn(
                  "size-3.5 shrink-0 text-muted-foreground transition-transform",
                  groupOpen && "rotate-90",
                )}
              />
              <span className="type-body font-medium text-foreground">{axisNoun(axis)}</span>
              <span className="ml-auto type-subtext tabular-nums text-muted-foreground">{left} left</span>
            </button>
            {groupOpen && (
              <ul className="pb-1">
                {list.map((c) => {
                  const done = c.key in optimistic;
                  const active = clusters.indexOf(c) === cursor;
                  return (
                    <li key={c.key}>
                      <button
                        type="button"
                        onClick={() => onPick(clusters.indexOf(c))}
                        className={cn(
                          "flex w-full items-center gap-2 py-1.5 pr-3 pl-9 text-left transition-colors hover:bg-muted/40",
                          active && "bg-selected",
                        )}
                      >
                        <span
                          className={cn(
                            "size-1.5 shrink-0 rounded-full",
                            done ? "bg-success" : active ? "bg-foreground" : "bg-muted-foreground/40",
                          )}
                        />
                        <span
                          className={cn(
                            "truncate type-subtext",
                            done ? "text-muted-foreground line-through" : "text-foreground",
                          )}
                        >
                          {cap(c.a)} <span className="text-muted-foreground">or</span> {cap(c.b)}
                        </span>
                        <span className="ml-auto shrink-0 type-subtext tabular-nums text-muted-foreground">
                          {c.members.length}
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        );
      })}
    </div>
  );
}

function Kbd({ children }: { children: React.ReactNode }) {
  return (
    // badge-ignore: keyboard key chrome (⌘K affordance), not a categorical status tag
    <span className="rounded-sm border border-border bg-surface-2 px-1 type-subtext tabular-nums text-muted-foreground">
      {children}
    </span>
  );
}

function ListMode({
  clusters,
  optimistic,
  busy,
  onSettle,
}: {
  clusters: Cluster[];
  optimistic: Record<string, string | null>;
  busy: string | null;
  onSettle: (c: Cluster, v: string | null) => void;
}) {
  return (
    <CardList
      title="Every open decision"
      count={clusters.filter((c) => !(c.key in optimistic)).length}
      subtitle="biggest first — the number is how many examples one answer settles"
    >
      {clusters.map((c) => {
        const done = c.key in optimistic;
        return (
          <RowShell key={c.key} className={cn("gap-3", done && "opacity-55")}>
            <IntentBadge intent="info">{axisNoun(c.axis)}</IntentBadge>
            <span className="min-w-0 flex-1 truncate type-body text-foreground">
              {c.members[0]?.title}
            </span>
            <span className="shrink-0 type-subtext tabular-nums text-muted-foreground">
              {c.members.length}
            </span>
            {done ? (
              <span className="flex shrink-0 items-center gap-1.5 type-subtext text-muted-foreground">
                <Check className="size-3.5 text-success-fg" />
                {optimistic[c.key] ? cap(optimistic[c.key]!) : "None"}
              </span>
            ) : (
              <span className="flex shrink-0 items-center gap-1.5">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={busy === c.key}
                  onClick={() => onSettle(c, c.a === NONE ? null : c.a)}
                >
                  {cap(c.a)}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={busy === c.key}
                  onClick={() => onSettle(c, c.b === NONE ? null : c.b)}
                >
                  {cap(c.b)}
                </Button>
              </span>
            )}
          </RowShell>
        );
      })}
    </CardList>
  );
}

// ── Health ────────────────────────────────────────────────────────────────────

function HealthTab({ detail }: { detail: CompilationDetail }) {
  const total = detail.corpusTotal;
  const rates: Record<string, number> = {
    auto: detail.routing.auto,
    tier2: detail.routing.tier2,
    human: detail.routing.human,
  };
  const epochsAsc = [...detail.epochs].sort((a, b) => a.epoch - b.epoch);
  const first = epochsAsc[0];
  const last = epochsAsc[epochsAsc.length - 1];
  const trend = first
    ? [first.holdoutBefore, ...epochsAsc.map((e) => e.holdoutAfter)]
    : [];
  const gain = first && last ? last.holdoutAfter - first.holdoutBefore : 0;
  const coverage = detail.axisCoverage;
  const weakest = [...coverage].sort((a, b) => a.coverage - b.coverage)[0];
  const lastPassLabel = detail.lastEpochStartedAt
    ? formatRelativeTime(detail.lastEpochStartedAt)
    : detail.lastEpoch != null
      ? `epoch #${detail.lastEpoch}`
      : "never";

  return (
    <Card className="gap-0 overflow-hidden py-0">
      <div className="grid gap-px bg-border/60 @2xl/page:grid-cols-3">
        <div className="flex flex-col gap-3 bg-card px-5 py-3">
          <div className="flex items-baseline justify-between">
            <h2 className="type-title font-medium text-foreground">Routing</h2>
            <span className="type-subtext text-muted-foreground">{total} examples</span>
          </div>
          <div className="flex h-2.5 w-full overflow-hidden rounded-full bg-foreground/10">
            {LADDER.map((seg) => {
              const rate = rates[seg.key] ?? 0;
              if (rate <= 0) return null;
              return (
                <span
                  key={seg.key}
                  className={cn("h-full", seg.fill)}
                  style={{ width: `${rate * 100}%` }}
                />
              );
            })}
          </div>
          <ul className="flex flex-col gap-2">
            {LADDER.map((seg, i) => {
              const rate = rates[seg.key] ?? 0;
              const n = Math.round(rate * total);
              return (
                <li key={seg.key} className="flex items-baseline gap-2 type-body">
                  <span
                    className={cn(
                      "flex size-4 shrink-0 items-center justify-center self-center rounded-full type-subtext font-medium text-background",
                      seg.dot,
                    )}
                  >
                    {i + 1}
                  </span>
                  <span className="text-foreground">{seg.label}</span>
                  <span className="ml-auto tabular-nums text-foreground">{n}</span>
                  <span className="w-10 text-right tabular-nums text-muted-foreground">
                    {pct0(rate)}
                  </span>
                </li>
              );
            })}
          </ul>
        </div>

        <div className="flex flex-col gap-3 bg-card px-5 py-3">
          <div className="flex items-baseline justify-between">
            <h2 className="type-title font-medium text-foreground">Accuracy</h2>
            <span className="type-subtext text-muted-foreground">sealed split</span>
          </div>
          <div className="flex items-end gap-3">
            <span className="type-heading font-medium tabular-nums text-foreground">
              {pct1(detail.routing.holdoutAccuracy)}
            </span>
            {gain !== 0 && (
              <IntentBadge intent={gain > 0 ? "success" : "danger"}>
                {gain > 0 ? "+" : ""}
                {(gain * 100).toFixed(1)} pts
              </IntentBadge>
            )}
          </div>
          {trend.length > 1 && (
            <Sparkline values={trend} color="var(--chart-2)" className="h-16 w-full" ariaHidden />
          )}
          <p className="type-subtext text-muted-foreground">
            {epochsAsc.length} Pass{epochsAsc.length === 1 ? "" : "es"} · train{" "}
            {pct1(detail.routing.trainAccuracy)} · holdout n{" "}
            {detail.corpus.bySplit.holdout}
          </p>
        </div>

        <div className="flex flex-col gap-3 bg-card px-5 py-3">
          <div className="flex items-baseline justify-between">
            <h2 className="type-title font-medium text-foreground">Coverage</h2>
            <span className="type-subtext text-muted-foreground">engine holdout</span>
          </div>
          <ul className="flex flex-col gap-2.5">
            {coverage.map((c) => (
              <li key={c.axis} className="flex flex-col gap-1">
                <div className="flex items-baseline gap-2 type-subtext">
                  <span className="text-foreground">{axisNoun(c.axis)}</span>
                  <span className="text-muted-foreground">
                    {c.rules} rule{c.rules === 1 ? "" : "s"}
                  </span>
                  <span className="ml-auto tabular-nums text-muted-foreground">
                    {pct0(c.coverage)}
                  </span>
                </div>
                <HealthBar
                  value={c.coverage}
                  max={1}
                  intent={
                    c.coverage >= 0.6 ? "success" : c.coverage >= 0.2 ? "warning" : "danger"
                  }
                />
              </li>
            ))}
          </ul>
          {weakest && (
            <p className="type-subtext text-muted-foreground">
              Weakest: {axisNoun(weakest.axis)} at {pct0(weakest.coverage)} — caps auto-apply.
            </p>
          )}
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-x-6 gap-y-2 border-t border-border px-5 py-3 type-subtext text-muted-foreground">
        <span>
          Last Pass{" "}
          <span className="text-foreground">
            {detail.lastEpoch != null ? `#${detail.lastEpoch}` : "—"}
          </span>
          {detail.lastEpochStartedAt ? `, ${lastPassLabel}` : ""}
        </span>
        <span>
          Autorun{" "}
          <span className="text-foreground">
            {detail.autorun.enabled ? "on" : "off"}
          </span>{" "}
          · ceiling {detail.autorun.budgetTokens.toLocaleString()} tokens
        </span>
        <span>
          Labeled data <span className="text-foreground">{detail.corpusTotal}</span> ·{" "}
          {detail.corpus.byOrigin.real} from workspace
        </span>
        <span>
          {detail.promoted.at ? (
            <>
              Live since{" "}
              <span className="text-foreground">{formatDateTime(detail.promoted.at)}</span>
              {detail.promoted.taskId != null ? ` · #${detail.promoted.taskId}` : ""}
            </>
          ) : (
            <>Not live — promote with a verified task to apply rules</>
          )}
        </span>
      </div>
    </Card>
  );
}

// ── Rules (read-only) ─────────────────────────────────────────────────────────

function ruleAssigns(r: RuleView): string {
  const value = r.id.split(":").pop() ?? "";
  if (r.axis === "goalId") return "Links a task to a goal";
  return `Sets ${axisNoun(r.axis)} → ${cap(value)}`;
}

function RulesTab({
  detail,
  onGoLabel,
}: {
  detail: CompilationDetail;
  onGoLabel: () => void;
}) {
  const accepted = detail.rules.filter((r) => r.status === "accepted");
  const neverFire = accepted.filter((r) => r.holdoutN === 0).length;
  const overconfident = detail.calibration.filter((c) => c.accuracy < c.lo);
  const columns: DataTableColumn<RuleView>[] = [
    {
      key: "id",
      header: "Rule",
      width: "w-56",
      sort: (a, b) => a.id.localeCompare(b.id),
      render: (r) => <span className="font-mono type-subtext text-muted-foreground">{r.id}</span>,
    },
    {
      key: "assigns",
      header: "What it does",
      sort: (a, b) => ruleAssigns(a).localeCompare(ruleAssigns(b)),
      render: (r) => <span className="type-subtext text-foreground">{ruleAssigns(r)}</span>,
    },
    {
      key: "status",
      header: "Status",
      width: "w-24",
      sort: (a, b) => a.status.localeCompare(b.status),
      render: (r) => <IntentBadge intent={RULE_INTENT[r.status]}>{r.status}</IntentBadge>,
    },
    {
      key: "confidence",
      header: "Confidence",
      width: "w-28",
      sort: (a, b) => a.confidence - b.confidence,
      render: (r) => (
        <span className="type-subtext tabular-nums">
          {pct0(r.confidence)}
          {r.confidence < detail.autoThreshold ? (
            <span className="ml-1 text-muted-foreground">below line</span>
          ) : null}
        </span>
      ),
    },
    {
      key: "holdoutPrecision",
      header: "Right on sealed",
      width: "w-32",
      sort: (a, b) => (a.holdoutPrecision ?? -1) - (b.holdoutPrecision ?? -1),
      render: (r) =>
        r.holdoutPrecision === null || r.holdoutN === 0 ? (
          <span className="type-subtext text-muted-foreground">never fired</span>
        ) : (
          <span className="type-subtext tabular-nums">
            <span className={cn(r.holdoutPrecision < 0.8 && "text-destructive")}>
              {pct0(r.holdoutPrecision)}
            </span>
            <span className="text-muted-foreground"> of {r.holdoutN}</span>
          </span>
        ),
    },
  ];

  return (
    <div className="flex flex-col gap-4">
      <div className="grid gap-4 @2xl/page:grid-cols-2">
        <Card className="flex flex-col gap-3 p-5">
          <h2 className="type-title font-medium text-foreground">Confidence</h2>
          <p className="type-subtext text-muted-foreground">
            {accepted.filter((r) => r.confidence >= detail.autoThreshold).length} of{" "}
            {accepted.length} accepted rules clear auto ≥ {pct0(detail.autoThreshold)}.{" "}
            {neverFire} never fire on sealed examples.{" "}
            <button
              type="button"
              onClick={onGoLabel}
              className="font-medium text-primary underline-offset-2 hover:underline"
            >
              Label more →
            </button>
          </p>
        </Card>
        <Card className="flex flex-col gap-3 p-5">
          <h2 className="type-title font-medium text-foreground">Calibration</h2>
          {overconfident.length === 0 ? (
            <p className="type-subtext text-muted-foreground">
              Every confidence band performs at or above what it claims.
            </p>
          ) : (
            <ul className="flex flex-col gap-2">
              {overconfident.map((c) => (
                <li key={`${c.axis}-${c.lo}`} className="flex flex-col gap-1">
                  <div className="flex items-baseline gap-2 type-subtext">
                    <span className="text-foreground">{axisNoun(c.axis)}</span>
                    <span className="text-muted-foreground">
                      claims {pct0(c.lo)}–{pct0(c.hi)}
                    </span>
                    <span className="ml-auto tabular-nums text-destructive">
                      actually {pct0(c.accuracy)}
                    </span>
                  </div>
                  <HealthBar value={c.accuracy} max={1} intent="danger" />
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>
      <DataTable
        rows={accepted}
        columns={columns}
        rowKey={(r) => `${r.axis}-${r.id}`}
        sortable
        pageSize={12}
        initialSort={{ key: "confidence", dir: "desc" }}
        title="Rules it has learned"
        subtitle="Read-only — the optimizer proposes rules; the held-out gate keeps or rejects them."
        empty="No accepted rules yet."
      />
    </div>
  );
}

// ── Epochs / Corpus (from prior product surface) ──────────────────────────────

function EpochsTab({ epochs }: { epochs: EpochView[] }) {
  if (epochs.length === 0) {
    return (
      <Card className="gap-0 py-0">
        <p className="p-6 type-body text-muted-foreground">
          No passes yet. Use &ldquo;Run a Pass&rdquo; to dispatch an epoch.
        </p>
      </Card>
    );
  }
  const columns: DataTableColumn<EpochView>[] = [
    {
      key: "epoch",
      header: "Pass",
      width: "w-16",
      sort: (a, b) => a.epoch - b.epoch,
      render: (e) => <span className="type-subtext tabular-nums">#{e.epoch}</span>,
    },
    {
      key: "startedAt",
      header: "Started",
      sort: (a, b) => (a.startedAt ?? "").localeCompare(b.startedAt ?? ""),
      render: (e) => (
        <span className="type-subtext text-muted-foreground">
          {e.startedAt ? formatDateTime(e.startedAt) : "—"}
        </span>
      ),
    },
    {
      key: "holdout",
      header: "Holdout",
      sort: (a, b) => a.holdoutAfter - b.holdoutAfter,
      render: (e) => (
        <span className="type-subtext tabular-nums">
          {pct1(e.holdoutBefore)} → {pct1(e.holdoutAfter)}
        </span>
      ),
    },
    {
      key: "train",
      header: "Train",
      sort: (a, b) => a.trainAfter - b.trainAfter,
      render: (e) => (
        <span className="type-subtext tabular-nums">
          {pct1(e.trainBefore)} → {pct1(e.trainAfter)}
        </span>
      ),
    },
    {
      key: "edits",
      header: "Edits",
      render: (e) => (
        <span className="type-subtext tabular-nums text-muted-foreground">
          {e.acceptedCount} accepted · {e.rejectedCount} rejected · {e.proposed} proposed
        </span>
      ),
    },
    {
      key: "queue",
      header: "Labeled",
      render: (e) => (
        <span className="type-subtext tabular-nums text-muted-foreground">
          tier2 {e.tier2Labeled} · human {e.humanQueued}
        </span>
      ),
    },
  ];
  return (
    <DataTable
      rows={epochs}
      columns={columns}
      rowKey={(e) => String(e.epoch)}
      sortable
      initialSort={{ key: "epoch", dir: "desc" }}
      title="Pass ledger"
      empty="No passes."
    />
  );
}

function CorpusTab({ detail }: { detail: CompilationDetail }) {
  const c = detail.corpus;
  const columns: DataTableColumn<CorpusSampleRow>[] = [
    {
      key: "title",
      header: "Example",
      render: (r) => (
        <div className="min-w-0">
          <p className="truncate type-subtext font-medium text-foreground">{r.title}</p>
          <p className="truncate type-subtext text-muted-foreground">{r.exampleKey}</p>
        </div>
      ),
    },
    {
      key: "split",
      header: "Split",
      width: "w-24",
      sort: (a, b) => a.split.localeCompare(b.split),
      render: (r) => <IntentBadge intent={SPLIT_INTENT[r.split]}>{r.split}</IntentBadge>,
    },
    {
      key: "origin",
      header: "Origin",
      width: "w-24",
      sort: (a, b) => a.origin.localeCompare(b.origin),
      render: (r) => <span className="type-subtext text-muted-foreground">{r.origin}</span>,
    },
    {
      key: "truth",
      header: "Truth",
      render: (r) => (
        <span className="block max-w-md truncate type-subtext text-muted-foreground">
          {r.truthSummary}
        </span>
      ),
    },
  ];
  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap gap-4 type-subtext text-muted-foreground">
        <span>
          Total <span className="text-foreground tabular-nums">{c.total}</span>
        </span>
        <span>
          Train {c.bySplit.train} · Holdout {c.bySplit.holdout}
        </span>
        <span>
          Synthetic {c.byOrigin.synthetic} · Real {c.byOrigin.real}
        </span>
      </div>
      <DataTable
        rows={c.sample}
        columns={columns}
        rowKey={(r) => r.exampleKey}
        sortable
        pageSize={20}
        title="Corpus sample"
        subtitle="Most recently updated first (cap 50)."
        empty="No examples yet."
      />
    </div>
  );
}

// ── TanStack route ────────────────────────────────────────────────────────────

function CompiledWorkflowPage() {
  const { slug } = agentsWorkflowCompiledRoute.useParams();
  const { data, error, isLoading } = useApi(compilations.get, {
    params: { slug },
  });

  if (error) {
    // The original's `notFound()` for unknown slugs. Rendered rather than thrown
    // so the shell chrome survives — a bad slug is a wrong turn, not a crash.
    const msg = error instanceof Error ? error.message : String(error);
    const isMissing =
      /unknown workflow|not found|404/i.test(msg) ||
      (typeof (error as { status?: number }).status === "number" &&
        (error as { status?: number }).status === 404);
    return (
      <div className="flex flex-col gap-3 px-6 py-8">
        <h1 className="type-heading font-medium text-foreground">
          {isMissing ? "No such compiled workflow" : "Could not load workflow"}
        </h1>
        <p className="type-body text-muted-foreground">
          {isMissing ? (
            <>
              Nothing in the vault is registered under{" "}
              <span className="font-mono">{slug}</span>.
            </>
          ) : (
            msg
          )}
        </p>
        <a
          href="/agents/workflows"
          className="inline-flex w-fit items-center gap-1 type-body text-muted-foreground underline-offset-4 hover:text-foreground hover:underline"
        >
          ← Workflows
        </a>
      </div>
    );
  }

  if (isLoading || !data) {
    return (
      <div className="px-6 py-8 type-body text-muted-foreground">Loading…</div>
    );
  }

  return (
    <div className="px-6 py-8">
      <CompiledDetail slug={slug} initial={data} />
    </div>
  );
}

export const agentsWorkflowCompiledRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/agents/workflows/compiled/$slug",
  component: CompiledWorkflowPage,
});
